
import Box from '@mui/material/Box';
import EDTypography from '../Typography/EDTypography';
import { IRapportCustomGridControl } from './RapportMallTypes';
import {
  MM_TO_PIXEL_SCALE,
  SELECTED_SHADOW,
  getPositionStyle,
} from './RapportMallUtils';
import RapportTypography from './RapportTypography';
import { RapportKontrollEditPosition } from './RapportKontrollEditPosition';
import { RapportKontrollHeader } from './RapportKontrollHeader';
import { RapporteditorTextField } from '../Rapporteditor/RapporteditorTextField';
import { RapporteditorAssociatedField } from '../Rapporteditor/RapporteditorAssociatedField';
import EDIconButton from '../Buttons/IconButton/EDIconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import Divider from '@mui/material/Divider';

const scale = MM_TO_PIXEL_SCALE;
const CUSTOMGRID_ROW_HEIGHT = 10;

export interface IProps {
  ctrl: IRapportCustomGridControl;
  sx?: any;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onSelect?: (path: number[]) => void;
  onUpdate?: (updatedControl: IRapportCustomGridControl) => void;
  controlPath: number[];
  selected?: boolean;
  maxWidth: number;
}

export const RapportCustomGrid: React.FC<IProps> = props => {
  const { ctrl, sx, onMouseEnter, onMouseLeave, selected, maxWidth } = props;
  const handleSelect = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (props.onSelect) {
      props.onSelect(props.controlPath);
    }
  };
  let rowHeight = CUSTOMGRID_ROW_HEIGHT;
  ctrl.Fields?.forEach(field => {
    if (field.NewRow) {
      rowHeight += CUSTOMGRID_ROW_HEIGHT;
    }
  });
  const rowCount = Math.min(
    Math.max(ctrl.MinRowCount ?? 0, ctrl.EmptyRowCount ?? 0, 1),
    ctrl.MaxRowCount ?? Infinity
  );
  const emptyRowStart = Math.max(rowCount - (ctrl.EmptyRowCount ?? 0), 0);
  const allocatedFieldWidths = ctrl.Fields
    ? ctrl.Fields.reduce((sum, field) => sum + (field.Width || 0), 0)
    : 0;
  const dynamicFieldCount = ctrl.Fields
    ? ctrl.Fields.filter(field => field.Width === undefined).length
    : 0;
  const availableWidth = ctrl.Width ?? maxWidth;
  const dynamicFieldWidth =
    dynamicFieldCount >= 1
      ? (availableWidth - allocatedFieldWidths) / dynamicFieldCount
      : 0;

  return (
    <Box
      sx={{
        boxShadow: selected ? SELECTED_SHADOW : undefined,
        boxSizing: 'border-box',
        padding: 0,
        ...getPositionStyle(ctrl, props.controlPath.length > 1, maxWidth),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        overflow: 'hidden',
        ...sx,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={handleSelect}
      data-control-path={props.controlPath.join('-')}
    >
      {Array.from({ length: rowCount }, (_, rowIndex) => {
        let x = 0;
        let y = 0;
        return (
          <Box
            key={rowIndex}
            sx={{
              height: rowHeight * scale,
              mt: rowIndex === 0 ? 0 : 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              position: 'relative',
            }}
          >
            {ctrl.Fields?.map((field, fieldIndex) => {
              if (field.NewRow) {
                x = 0;
                y += CUSTOMGRID_ROW_HEIGHT;
              }
              const left = x;
              const top = y;
              const width = field.Width || dynamicFieldWidth;
              x += width;
              return (
                <Box
                  key={fieldIndex}
                  sx={{
                    boxSizing: 'border-box',
                    padding: 0,
                    ...getPositionStyle(
                      {
                        Left: left,
                        Top: top,
                        Width: width,
                        Height: CUSTOMGRID_ROW_HEIGHT,
                      },
                      true,
                      maxWidth
                    ),
                    position: 'absolute',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    border: '1px solid #ccc',
                    background: field.EditBox ? '#e9eeff' : undefined,
                  }}
                >
                  {field.Caption && (
                    <EDTypography
                      variant="caption"
                      fontSize={10}
                      lineHeight={1}
                      sx={{
                        p: '2px 4px 0 4px',
                        m: 0,
                        minHeight: 13,
                      }}
                    >
                      {field.Caption}
                    </EDTypography>
                  )}
                  <Box
                    sx={{
                      flex: 1,
                      p: '0px 4px 2px 8px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {rowIndex < emptyRowStart && (
                      <RapportTypography lineHeight={CUSTOMGRID_ROW_HEIGHT}>
                        {field.Name ? `{${field.Name}}` : field.Caption}
                      </RapportTypography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

export interface IRapportTextBoxEditProps {
  value: IRapportCustomGridControl;
  onChange: (ctrl: IRapportCustomGridControl) => void;
}

export const RapportCustomGridEdit: React.FC<
  IRapportTextBoxEditProps
> = props => (
  <Box sx={{ mt: 2 }}>
    <Box sx={{ mb: 3 }}>
      <RapportKontrollHeader value={props.value} />
      <RapporteditorTextField
        label="Undertabell"
        field="Association"
        value={props.value}
        onChange={props.onChange}
        allowModelSelect
        selectAssociation
      />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <RapporteditorTextField
          label="Max antal rader"
          hint="Antal rader som högst ska visas i tabellen. Kan överstyra Minsta antal rader och Antal tomma rader."
          field="MaxRowCount"
          value={props.value}
          onChange={props.onChange}
          numeric
        />
        <RapporteditorTextField
          label="Minsta antal rader"
          hint="Antal rader som minst ska visas i tabellen. Fylls på med tomma rader om undertabellen har färre rader."
          field="MinRowCount"
          value={props.value}
          onChange={props.onChange}
          numeric
        />
        <RapporteditorTextField
          label="Antal tomma rader"
          hint="Antal tomma rader efter raderna från undertabellen."
          field="EmptyRowCount"
          value={props.value}
          onChange={props.onChange}
          numeric
        />
      </Box>
    </Box>
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <EDTypography variant="subtitle2">Fält</EDTypography>
        <EDIconButton
          icon={<AddCircleOutlineIcon />}
          onClick={() => {
            const newFields = [...(props.value.Fields || [])];
            newFields.push({
              Name: '',
              Caption: '',
              Width: 20,
              NewRow: false,
            });
            props.onChange({ ...props.value, Fields: newFields });
          }}
          margin="none"
          padding="none"
          tooltip="Lägg till nytt fält"
        />
      </Box>
      {props.value.Fields && props.value.Fields.length > 0 ? (
        props.value.Fields.map((field, index) => (
          <Box
            key={index}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
          >
            <Box sx={{ flex: 1 }}>
              {index > 0 && <Divider sx={{ marginBottom: 4 }} />}
              <RapporteditorAssociatedField
                field={field}
                customField
                association={props.value.Association}
                onFieldChange={updatedField => {
                  const newFields = [...(props.value.Fields || [])];
                  newFields[index] = updatedField;
                  props.onChange({ ...props.value, Fields: newFields });
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                mt: index > 0 ? 5 : 1,
              }}
            >
              <EDIconButton
                icon={<ArrowCircleUpIcon />}
                onClick={() => {
                  if (index > 0) {
                    const newFields = [...(props.value.Fields || [])];
                    [newFields[index - 1], newFields[index]] = [
                      newFields[index],
                      newFields[index - 1],
                    ];
                    props.onChange({ ...props.value, Fields: newFields });
                  }
                }}
                disabled={index === 0}
                margin="none"
                padding="none"
                tooltip="Flytta upp fält"
              />
              <EDIconButton
                icon={<RemoveCircleOutlineIcon />}
                onClick={() => {
                  const newFields = [...(props.value.Fields || [])];
                  newFields.splice(index, 1);
                  props.onChange({ ...props.value, Fields: newFields });
                }}
                margin="none"
                padding="none"
                tooltip="Ta bort fält"
              />
              <EDIconButton
                icon={<ArrowCircleDownIcon />}
                onClick={() => {
                  if (index < (props.value.Fields?.length || 0) - 1) {
                    const newFields = [...(props.value.Fields || [])];
                    [newFields[index], newFields[index + 1]] = [
                      newFields[index + 1],
                      newFields[index],
                    ];
                    props.onChange({ ...props.value, Fields: newFields });
                  }
                }}
                disabled={index === (props.value.Fields?.length || 0) - 1}
                margin="none"
                padding="none"
                tooltip="Flytta ner fält"
              />
            </Box>
          </Box>
        ))
      ) : (
        <EDTypography variant="body2" color="textSecondary">
          Tryck på plus-knappen för att lägga till fält.
        </EDTypography>
      )}
    </Box>
    <RapportKontrollEditPosition
      value={props.value}
      onChange={props.onChange}
    />
  </Box>
);
